import { Box, Typography } from '@mui/material';
import React from 'react';
import { formatNumber } from '~/components/NumberText';
import { formatUiDate } from '~/lib/date';

export type SegmentLike = {
  startDate?: string;
  endDate?: string;
  numOfDays?: number;
  peak?: number;
  offPeak?: number;
  shoulder?: number;
  lret?: number;
  sres?: number;
  ess?: number;
  veet?: number;
  aeeis?: number;
  pdrs?: number;
  reps?: number;
  peakConsumptionExcludeLoss?: number;
  offPeakConsumptionExcludeLoss?: number;
  shoulderConsumptionExcludeLoss?: number;
  totalConsumptionExcludeLoss?: number;
};

export function renderSegmentsTooltipContent(
  groups: { meterLabel?: string; segments: SegmentLike[] }[],
  keyPrefix: string
): React.ReactNode {
  if (groups.length === 0 || groups.every((group) => !group.segments?.length)) return null;

  return (
    <Box sx={{ p: 1, maxHeight: '400px', overflowY: 'auto' }}>
      {groups.map((group, groupIndex) =>
        (group.segments?.length ?? 0) > 0 ? (
          <Box key={`${keyPrefix}-${groupIndex}`} sx={{ mb: groupIndex < groups.length - 1 ? 2 : 0 }}>
            {group.meterLabel && (
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                {group.meterLabel}
              </Typography>
            )}
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: group.meterLabel ? 'normal' : 'bold' }}>
              Segments Details ({group.segments.length} segments)
            </Typography>
            {group.segments.map((segment, index) => (
              <Box
                key={index}
                sx={{
                  mb: 1.5,
                  pb: 1.5,
                  borderBottom: index < group.segments.length - 1 ? '1px solid rgba(255,255,255,0.2)' : 'none'
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                  Segment {index + 1}: {formatUiDate(segment.startDate)} - {formatUiDate(segment.endDate)} (
                  {segment.numOfDays} days)
                </Typography>
                <Box sx={{ pl: 1, fontSize: '0.75rem' }}>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    <strong>Contract Rates (c/kWh):</strong>
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', pl: 1 }}>
                    Peak: {formatNumber(segment.peak ?? 0, 4, '-')}, Off-Peak:{' '}
                    {formatNumber(segment.offPeak ?? 0, 4, '-')}, Shoulder:{' '}
                    {formatNumber(segment.shoulder ?? 0, 4, '-')}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    <strong>Environmental Rates (c/kWh):</strong>
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', pl: 1 }}>
                    LRET: {formatNumber(segment.lret ?? 0, 4, '-')}, SRES: {formatNumber(segment.sres ?? 0, 4, '-')},
                    ESS: {formatNumber(segment.ess ?? 0, 4, '-')}, VEET: {formatNumber(segment.veet ?? 0, 4, '-')},
                    AEEIS: {formatNumber(segment.aeeis ?? 0, 4, '-')}, PDRS: {formatNumber(segment.pdrs ?? 0, 4, '-')},
                    REPS: {formatNumber(segment.reps ?? 0, 4, '-')}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    <strong>Consumption kWh:</strong>
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', pl: 1 }}>
                    Peak: {formatNumber(segment.peakConsumptionExcludeLoss ?? 0, 0, '-')}, Off-Peak:{' '}
                    {formatNumber(segment.offPeakConsumptionExcludeLoss ?? 0, 0, '-')}, Shoulder:{' '}
                    {formatNumber(segment.shoulderConsumptionExcludeLoss ?? 0, 0, '-')}, Total:{' '}
                    {formatNumber(segment.totalConsumptionExcludeLoss ?? 0, 0, '-')}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        ) : null
      )}
    </Box>
  );
}
